import { createStore } from "./store";

// Core Slices
import { createAuthSlice, AuthSlice } from "./slices/auth";
import { createEncryptionSlice, EncryptionSlice } from "./slices/encryption";
import { createUserSlice, UserSlice } from "./slices/user";
import { createThreadSlice as createThreadMapSlice, ThreadSlice as ThreadMapSlice } from "./slices/group";
import { createHiloSlice, HiloSlice } from "./slices/hilo";
import { createThreadSlice, ThreadSlice } from "./slices/thread";
import { createProfilePhotoSlice, ProfilePhotoSlice } from "./slices/profilePhoto";

// Complex Slices
import { createCommunitySlice, CommunitySlice } from "./slices/community";
import { createRecoverySlice, RecoverySlice } from "./slices/recovery";

// Remaining Slices
import { createCreditSlice, CreditSlice } from "./slices/credit";
import { createLlmSlice, LlmSlice } from "./slices/llm";
import { createDialogSlice, DialogSlice } from "./slices/dialog";

// Combined store type - all slices
export type AppStore = AuthSlice &
  EncryptionSlice &
  UserSlice &
  ProfilePhotoSlice &
  ThreadMapSlice &
  HiloSlice &
  ThreadSlice &
  CommunitySlice &
  RecoverySlice &
  CreditSlice &
  LlmSlice &
  DialogSlice;

// Create the store with middleware
export const useStore = createStore<AppStore>((...args) => ({
  // Core slices
  ...createAuthSlice(...args),
  ...createEncryptionSlice(...args),
  ...createUserSlice(...args),
  ...createProfilePhotoSlice(...args),
  ...createThreadMapSlice(...args),
  ...createHiloSlice(...args),
  // Complex slices
  ...createThreadSlice(...args),
  ...createCommunitySlice(...args),
  ...createRecoverySlice(...args),
  // Remaining slices
  ...createCreditSlice(...args),
  ...createLlmSlice(...args),
  ...createDialogSlice(...args),
}));

// Export types - Core
export type { AuthSlice } from "./slices/auth";
export type { EncryptionSlice, EncryptionKeyPair, KeyPair } from "./slices/encryption";
export type { UserSlice } from "./slices/user";
export type { ThreadSlice as ThreadMapSlice } from "./slices/group";
export type { HiloSlice, HiloEnvironment } from "./slices/hilo";

// Export types - Complex
export type { ThreadSlice } from "./slices/thread";
export type { CommunitySlice } from "./slices/community";
export type {
  RecoverySlice,
  RecoveryStep,
  SetupStep,
  VerificationWord,
  AuthKeyPair,
} from "./slices/recovery";
export type { LoadingState, CommunityMember, GroupMember } from "./slices/thread";

// Export types - Remaining
export type { CreditSlice, CreditHistoryEntry, IapProduct } from "./slices/credit";
export type { LlmSlice, AiMessage, AiMessages } from "./slices/llm";
export type { DialogSlice, DialogState } from "./slices/dialog";
